// Unidades.tsx
//
// Por unidade: COORDENADA, CONTATO e o interruptor de PEDIDO ONLINE.
//
// A coordenada é o pré-requisito da "unidade mais próxima" e é o campo de maior
// risco silencioso desta tela: um ponto errado manda o cliente para a unidade
// errada e nenhum erro aparece em lugar nenhum. Por isso a tela força a
// conferência humana — o pino no mapa abre em outra aba e só depois de o dono
// confirmar é que a unidade passa a ser ordenada por distância.
//
// O interruptor de pedido online só liga com o vínculo completo (tipo de caixa,
// balcão e usuário do PDV). O motivo é duro: `aod` é MONO-LOJA — AotOrder,
// AotPeriod, AotItem e AotUser não têm coluna de filial. Sem esse vínculo, um
// pedido "para o Villa" entraria no caixa de quem estivesse operando.
//
// Os três uuids vêm de SELECT, nunca digitados: um caractere trocado num campo
// de texto seria exatamente o erro que este vínculo existe para impedir.

import React, { useEffect, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import { siteService } from "../../../../services/site.service";
import { UnidadeDaLanding, coordenadaValida, linkDoMapa } from "../../../../comum/site";
import SecaoDeEntrega from "./SecaoDeEntrega";

interface Props {
  unidades: UnidadeDaLanding[];
  onSalvo: () => void;
}

interface Rascunho {
  lat: string;
  lng: string;
  fonte: string;
  conferida: boolean;
  telefone: string;
  whatsapp: string;
  aceitaPedido: boolean;
  periodTypes: string[];
  balcaoUuid: string;
  usuarioUuid: string;
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

const Unidades: React.FC<Props> = ({ unidades, onSalvo }) => {
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [opcoes, setOpcoes] = useState<Record<string, any>>({});
  const [avisos, setAvisos] = useState<Record<string, Aviso>>({});
  const [salvando, setSalvando] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let ativo = true;
    for (const unidade of unidades) {
      const uuid = unidade.subsidiaryUuid;

      siteService
        .buscarUnidade(uuid)
        .then((dados) => {
          if (!ativo) return;
          setRascunhos((atuais) => ({
            ...atuais,
            [uuid]: {
              lat: dados?.coordenada?.lat != null ? String(dados.coordenada.lat) : "",
              lng: dados?.coordenada?.lng != null ? String(dados.coordenada.lng) : "",
              fonte: dados?.coordenada?.fonte ?? "manual",
              conferida: Boolean(dados?.coordenada?.conferidoPor),
              telefone: dados?.contato?.telefone ?? "",
              whatsapp: dados?.contato?.whatsapp ?? "",
              aceitaPedido: dados?.operacional?.aceitaPedido === true,
              periodTypes: dados?.operacional?.periodTypes ?? [],
              balcaoUuid: dados?.operacional?.balcaoUuid ?? "",
              usuarioUuid: dados?.operacional?.usuarioUuid ?? "",
            },
          }));
        })
        .catch(() => {
          /* a aba funciona sem os dados de uma unidade; não vale branquear a tela */
        });

      siteService
        .opcoesDaUnidade(uuid)
        .then((dados) => {
          if (ativo) setOpcoes((atuais) => ({ ...atuais, [uuid]: dados }));
        })
        .catch(() => {
          /* sem as opções do PDV o interruptor fica desabilitado, e o motivo aparece */
        });
    }
    return () => {
      ativo = false;
    };
  }, [unidades]);

  const mudar = (uuid: string, mudanca: Partial<Rascunho>) => {
    setRascunhos((atuais) => ({
      ...atuais,
      [uuid]: { ...(atuais[uuid] as Rascunho), ...mudanca },
    }));
  };

  const salvar = async (unidade: UnidadeDaLanding) => {
    const uuid = unidade.subsidiaryUuid;
    const rascunho = rascunhos[uuid];
    if (!rascunho) return;

    setSalvando((atuais) => ({ ...atuais, [uuid]: true }));
    try {
      const corpo: any = {
        contato: { versao: 1, telefone: rascunho.telefone, whatsapp: rascunho.whatsapp },
        operacional: {
          aceitaPedido: rascunho.aceitaPedido,
          periodTypes: rascunho.periodTypes,
          balcaoUuid: rascunho.balcaoUuid || null,
          usuarioUuid: rascunho.usuarioUuid || null,
        },
      };

      // Coordenada só entra no corpo quando os dois campos existem: mandar
      // metade apagaria a que já está lá.
      if (rascunho.lat && rascunho.lng) {
        corpo.coordenada = {
          versao: 1,
          lat: Number(rascunho.lat.replace(",", ".")),
          lng: Number(rascunho.lng.replace(",", ".")),
          // Conferir pelo mapa é o que transforma um ponto derivado (fonte "s2")
          // em coordenada que ORDENA por distância.
          fonte: rascunho.conferida ? "manual" : rascunho.fonte,
          conferidoPor: rascunho.conferida ? "conferido-na-tela" : null,
        };
      }

      const resposta = await siteService.salvarUnidade(uuid, corpo);

      // Dois avisos possíveis, e os dois importam:
      //  · `aviso`       — pedi para ligar o pedido e NÃO deu (falta vínculo)
      //  · `avisoDoSite` — ligou, mas o site publicado não tem o botão ainda
      const doServidor = resposta?.aviso ?? resposta?.avisoDoSite;
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: doServidor
          ? { tom: "atencao", texto: doServidor }
          : { tom: "ok", texto: "Unidade salva." },
      }));
      onSalvo();
    } catch (erro: any) {
      setAvisos((atuais) => ({
        ...atuais,
        [uuid]: {
          tom: "erro",
          texto: erro?.response?.data?.message ?? "Não foi possível salvar a unidade.",
        },
      }));
    } finally {
      setSalvando((atuais) => ({ ...atuais, [uuid]: false }));
    }
  };

  if (unidades.length === 0) {
    return (
      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>Unidades</AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoSubtitulo>
          Esta marca ainda não tem nenhuma loja cadastrada. Cadastre em "Lojas".
        </AlfredoStyles.CartaoSubtitulo>
      </AlfredoStyles.Cartao>
    );
  }

  return (
    <>
      {unidades.map((unidade) => {
        const uuid = unidade.subsidiaryUuid;
        const rascunho = rascunhos[uuid];
        const daUnidade = opcoes[uuid];
        const aviso = avisos[uuid];

        if (!rascunho) {
          return (
            <AlfredoStyles.Cartao key={uuid}>
              <AlfredoStyles.CartaoTitulo>{unidade.nome}</AlfredoStyles.CartaoTitulo>
              <AlfredoStyles.LoadingContainer>
                <AlfredoStyles.LoadingSpinner />
                <div>Carregando…</div>
              </AlfredoStyles.LoadingContainer>
            </AlfredoStyles.Cartao>
          );
        }

        const lat = Number(rascunho.lat.replace(",", "."));
        const lng = Number(rascunho.lng.replace(",", "."));
        const temPonto = rascunho.lat !== "" && rascunho.lng !== "";
        const pontoValido = temPonto && coordenadaValida({ lat, lng });

        const faltando: string[] = [];
        if (rascunho.periodTypes.length === 0) faltando.push("os turnos de caixa desta unidade");
        if (!rascunho.balcaoUuid) faltando.push("o balcão de retirada");
        if (!rascunho.usuarioUuid) faltando.push("o usuário do PDV");

        return (
          <AlfredoStyles.Cartao key={uuid}>
            <AlfredoStyles.CartaoTitulo>{unidade.nome}</AlfredoStyles.CartaoTitulo>
            <AlfredoStyles.CartaoSubtitulo>
              {unidade.endereco ?? "Sem endereço no cadastro."}
            </AlfredoStyles.CartaoSubtitulo>

            {/* --- coordenada --- */}
            <AlfredoStyles.Linha style={{ alignItems: "flex-end" }}>
              <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 170)" }}>
                <span>Latitude</span>
                <AlfredoStyles.Entrada
                  value={rascunho.lat}
                  onChange={(e) => mudar(uuid, { lat: e.target.value, conferida: false })}
                  placeholder="-23.533305"
                />
              </AlfredoStyles.Campo>
              <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 170)" }}>
                <span>Longitude</span>
                <AlfredoStyles.Entrada
                  value={rascunho.lng}
                  onChange={(e) => mudar(uuid, { lng: e.target.value, conferida: false })}
                  placeholder="-46.711886"
                />
              </AlfredoStyles.Campo>
              {pontoValido && (
                <a
                  href={linkDoMapa({ lat, lng })}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginBottom: "var(--sp-8)" }}
                >
                  Ver o pino no mapa
                </a>
              )}
            </AlfredoStyles.Linha>

            {temPonto && !pontoValido && (
              <AlfredoStyles.Aviso $tom="erro">
                Esse ponto não fica no Brasil. Confira se a latitude e a longitude não
                estão trocadas — invertidas, elas mandam o cliente para a unidade errada
                sem nenhum erro aparecer.
              </AlfredoStyles.Aviso>
            )}

            <AlfredoStyles.LinhaCaixa style={{ marginTop: "var(--sp-10)" }}>
              <AlfredoStyles.Caixa
                type="checkbox"
                disabled={!pontoValido}
                checked={rascunho.conferida}
                onChange={(e) => mudar(uuid, { conferida: e.target.checked })}
              />
              <span>
                Abri o mapa e o pino está na porta desta unidade
                {!rascunho.conferida && (
                  <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
                    Sem esta confirmação a unidade aparece na lista, mas NÃO entra na
                    ordenação por distância. O endereço cadastrado é de rua, sem número:
                    o ponto derivado dele erra centenas de metros.
                  </AlfredoStyles.CampoAjuda>
                )}
              </span>
            </AlfredoStyles.LinhaCaixa>

            {/* --- contato --- */}
            <AlfredoStyles.Linha style={{ marginTop: "var(--sp-16)" }}>
              <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 200)" }}>
                <span>Telefone</span>
                <AlfredoStyles.Entrada
                  value={rascunho.telefone}
                  onChange={(e) => mudar(uuid, { telefone: e.target.value })}
                  placeholder="(11) 3641-3226"
                />
              </AlfredoStyles.Campo>
              <AlfredoStyles.Campo style={{ maxWidth: "calc(var(--u) * 200)" }}>
                <span>WhatsApp (com DDI)</span>
                <AlfredoStyles.Entrada
                  value={rascunho.whatsapp}
                  onChange={(e) => mudar(uuid, { whatsapp: e.target.value })}
                  placeholder="5511999999999"
                />
              </AlfredoStyles.Campo>
            </AlfredoStyles.Linha>

            {/* --- pedido online --- */}
            <AlfredoStyles.Linha style={{ marginTop: "var(--sp-16)" }}>
              {/* TURNOS, no plural. A loja instalada tem "Almoço" e "Jantar":
                  com um seletor de escolha única, o pedido seria recusado num
                  dos dois turnos todo dia. Marque todos em que esta unidade
                  opera. */}
              <AlfredoStyles.Campo as="div" style={{ minWidth: "calc(var(--u) * 240)" }}>
                <span>Turnos de caixa desta unidade</span>
                {(daUnidade?.tiposDeCaixa ?? []).map((item: any) => (
                  <AlfredoStyles.LinhaCaixa key={item.periodTypeUuid}>
                    <AlfredoStyles.Caixa
                      type="checkbox"
                      checked={rascunho.periodTypes.includes(item.periodTypeUuid)}
                      onChange={(e) =>
                        mudar(uuid, {
                          periodTypes: e.target.checked
                            ? [...rascunho.periodTypes, item.periodTypeUuid]
                            : rascunho.periodTypes.filter((t) => t !== item.periodTypeUuid),
                        })
                      }
                    />
                    <span>{item.periodTypeName}</span>
                  </AlfredoStyles.LinhaCaixa>
                ))}
                {(daUnidade?.tiposDeCaixa ?? []).length === 0 && (
                  <AlfredoStyles.CampoAjuda>
                    Nenhum turno de caixa cadastrado no PDV.
                  </AlfredoStyles.CampoAjuda>
                )}
              </AlfredoStyles.Campo>

              <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 210)" }}>
                <span>Balcão de retirada</span>
                <AlfredoStyles.Seletor
                  value={rascunho.balcaoUuid}
                  onChange={(e) => mudar(uuid, { balcaoUuid: e.target.value })}
                >
                  <option value="">—</option>
                  {(daUnidade?.balcoes ?? []).map((item: any) => (
                    <option key={item.itemUuid} value={item.itemUuid}>
                      {item.itemName}
                    </option>
                  ))}
                </AlfredoStyles.Seletor>
              </AlfredoStyles.Campo>

              <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 210)" }}>
                <span>Usuário do PDV</span>
                <AlfredoStyles.Seletor
                  value={rascunho.usuarioUuid}
                  onChange={(e) => mudar(uuid, { usuarioUuid: e.target.value })}
                >
                  <option value="">—</option>
                  {(daUnidade?.usuarios ?? []).map((item: any) => (
                    <option key={item.userUuid} value={item.userUuid}>
                      {item.userName}
                    </option>
                  ))}
                </AlfredoStyles.Seletor>
              </AlfredoStyles.Campo>
            </AlfredoStyles.Linha>

            <AlfredoStyles.LinhaCaixa style={{ marginTop: "var(--sp-12)" }}>
              <AlfredoStyles.Caixa
                type="checkbox"
                disabled={faltando.length > 0}
                checked={rascunho.aceitaPedido}
                onChange={(e) => mudar(uuid, { aceitaPedido: e.target.checked })}
              />
              <span>
                Esta unidade recebe pedidos pelo site
                {faltando.length > 0 && (
                  <AlfredoStyles.CampoAjuda style={{ display: "block" }}>
                    Ainda falta {faltando.join(", ")}. Sem os três, o pedido feito no
                    site entraria no caixa de outra loja — a landing continua mostrando
                    o telefone desta unidade.
                  </AlfredoStyles.CampoAjuda>
                )}
              </span>
            </AlfredoStyles.LinhaCaixa>

            <AlfredoStyles.Linha style={{ marginTop: "var(--sp-16)" }}>
              <AlfredoStyles.Button
                type="button"
                disabled={salvando[uuid] || (temPonto && !pontoValido)}
                onClick={() => salvar(unidade)}
              >
                {salvando[uuid] ? "Salvando…" : "Salvar unidade"}
              </AlfredoStyles.Button>
            </AlfredoStyles.Linha>

            {aviso && <AlfredoStyles.Aviso $tom={aviso.tom}>{aviso.texto}</AlfredoStyles.Aviso>}

            {/* ENTREGA — o Alfredo Rota.
                Fica no MESMO cartão da unidade, abaixo do pedido online, porque
                é a continuação da mesma decisão: aqui o dono liga o pedido, ali
                ele decide o que o cliente vê enquanto o pedido está a caminho.
                Os usuários do PDV vêm da mesma chamada `/site/unidade/:uuid/opcoes`
                que o vínculo do balcão já usa — uma consulta, dois usos. */}
            <SecaoDeEntrega
              subsidiaryUuid={uuid}
              usuariosDoPdv={daUnidade?.usuarios ?? []}
            />
          </AlfredoStyles.Cartao>
        );
      })}
    </>
  );
};

export default Unidades;
